/**
 * Pick the label to render for an extracted field in the active locale.
 *
 * Both labels ride on every `ExtractedDataItem` instead of the backend
 * resolving one against `Accept-Language`: the results payload is cached by
 * TanStack Query under a key that carries no locale, so a server-resolved
 * label stayed in the previous language until a hard reload refetched it.
 * Resolving here re-renders the moment the locale changes.
 *
 * Falls back to the Arabic (primary) label, then to the `key` — an English UI
 * must still name the field even when nobody filled the optional English name
 * in.
 *
 * Every candidate is de-slugified, not just the `key` fallback. A free-form
 * field's label is whatever the model read off the page, and it routinely
 * answers with the underscored slug it put in `key` rather than the printed
 * form the prompt asked for — which rendered "الاستمارة_رقم" on an operator's
 * card, because a non-empty label used to short-circuit this. The pipeline
 * folds those on the way in now; this stays the only defence for results
 * stored before it, and underscores are never part of a printed label.
 */
export function resolveFieldLabel(
  field: { key: string; label?: string | null; label_en?: string | null },
  locale: string
): string {
  if (locale === 'en') {
    const english = deSlugify(field.label_en);
    if (english) return english;
  }

  const primary = deSlugify(field.label);
  if (primary) return primary;

  return deSlugify(field.key);
}

function deSlugify(value?: string | null): string {
  return (value ?? '').replace(/[_\s]+/g, ' ').trim();
}
